/**
 * Ult (ท่าใหญ่) shape templates: five reusable shapes a whole art's ult is built from (A6 skeleton, speed mode). Each template
 * is a list of beats at fractions of the piece; `buildUlt` stretches them to the art's `len`, scales blows / reach, and warps the
 * rhythm, so two arts on one template still differ. Shares in every template add up to 1. Data only.
 *
 * TODO-polish: beats reuse the tempest (tp*) and taiji (tg*) pose / FX / sfx keys, so the client plays those generic poses and
 * FX until each art gets its own keys (the client falls back to the move's swing for a pose it has no motion for).
 */
import type { LongBeat, LongMoveDef, LongOpener } from './types';

export type UltTemplate = 'rooted' | 'dash' | 'leap' | 'spin' | 'throw';

/** A template beat: `f` = start as a fraction of the piece length; the rest as a LongBeat. */
type TBeat = Omit<LongBeat, 'at'> & { readonly f: number };

const TEMPLATES: Record<UltTemplate, readonly TBeat[]> = {
  // rooted pull + barrage (taiji-like): the caster never leaves the ground, the foe is moved
  rooted: [
    { f: 0, pose: 'tgDraw', hits: 1, share: 0.05, foe: 'pull', fx: 'tgMistDraw', sfx: 'tgDraw', cam: 'push', stop: 0.05 },
    { f: 0.1, pose: 'tgYieldL', hits: 1, share: 0.05, foe: 'spin', ai: 2, fx: 'tgMistArcL', stop: 0.04 },
    { f: 0.2, pose: 'tgYieldR', hits: 1, share: 0.05, foe: 'spin', ai: 2, fx: 'tgMistArcR', stop: 0.04 },
    { f: 0.3, pose: 'tgTurnBack', hits: 1, share: 0.09, foe: 'kneel', fx: 'tgMistFold', stop: 0.1 },
    { f: 0.4, pose: 'tgPalmRain', hits: 8, share: 0.22, foe: 'hit', ai: 4, fx: 'tgPalmRain', stop: 0.025 },
    { f: 0.58, pose: 'tgRoot', hits: 0, share: 0, foe: 'hit', fx: 'tgDomeGather', cam: 'pull' },
    { f: 0.7, pose: 'tgCrush', hits: 1, share: 0.54, foe: 'knock', dist: 2.4, ai: 2, fx: 'tgPressureWave', cam: 'shake', stop: 0.18 },
    { f: 0.8, pose: 'tgSettle', hits: 0, share: 0, foe: 'fall', fx: 'tgMistSettle', sfx: 'tgMistSettle' },
    { f: 0.9, pose: 'tgStance', hits: 0, share: 0, foe: 'recover', fx: 'tgRingFade', sfx: 'tgExhale' },
  ],
  // dash passes + vortex (tempest-like): the caster passes through the foe, lifts it, dives
  dash: [
    { f: 0, pose: 'tpUproot', hits: 1, share: 0.06, foe: 'lift', h: 0.6, fx: 'tpLeafSpout', sfx: 'tpUproot', cam: 'push', stop: 0.06 },
    { f: 0.09, pose: 'tpPassA', hits: 1, share: 0.07, foe: 'spin', move: { kind: 'pass', dist: 3.2 }, ai: 3, fx: 'tpWindLine', sfx: 'tpPass1', stop: 0.04 },
    { f: 0.18, pose: 'tpPassB', hits: 1, share: 0.07, foe: 'spin', move: { kind: 'pass', dist: 3.2 }, ai: 3, fx: 'tpWindLine', sfx: 'tpPass2', stop: 0.04 },
    { f: 0.27, pose: 'tpPassC', hits: 1, share: 0.07, foe: 'lift', h: 1.2, move: { kind: 'pass', dist: 3.2 }, ai: 3, fx: 'tpWindTriangle', sfx: 'tpPass3', cam: 'orbit', stop: 0.04 },
    { f: 0.36, pose: 'tpRise', hits: 4, share: 0.18, foe: 'lift', h: 1.8, move: { kind: 'leap', h: 2.4 }, fx: 'tpLeafVortex', sfx: 'tpVortexLoop', cam: 'orbit', stop: 0.06 },
    { f: 0.57, pose: 'tpGather', hits: 0, share: 0, foe: 'lift', h: 1.8, move: { kind: 'hang', h: 2.4 }, fx: 'tpLeafGather', sfx: 'tpWindDraw', cam: 'pull' },
    { f: 0.68, pose: 'tpDive', hits: 1, share: 0.55, foe: 'knock', dist: 1.6, move: { kind: 'dive' }, ai: 3, fx: 'tpGroveBurst', sfx: 'tpFinisher', cam: 'shake', stop: 0.16 },
    { f: 0.78, pose: 'tpKneel', hits: 0, share: 0, foe: 'fall', fx: 'tpLeafRain', sfx: 'tpLeafRain' },
    { f: 0.88, pose: 'tpStance', hits: 0, share: 0, foe: 'recover', fx: 'tpRingFade', sfx: 'tpSheathe' },
  ],
  // leap + slam: crouch, spring high with the foe hanging, a first crash, aftershocks, a second, bigger slam
  leap: [
    { f: 0, pose: 'tpUproot', hits: 1, share: 0.04, foe: 'hit', fx: 'tpLeafSpout', sfx: 'tpUproot', cam: 'push', stop: 0.05 },
    { f: 0.12, pose: 'tpRise', hits: 0, share: 0, foe: 'lift', h: 1.2, move: { kind: 'leap', h: 2.4 }, ai: 3, fx: 'tpLeafVortex', sfx: 'tpPass1', cam: 'orbit' },
    { f: 0.3, pose: 'tpGather', hits: 0, share: 0, foe: 'lift', h: 1.4, move: { kind: 'hang', h: 2.4 }, fx: 'tpLeafGather', sfx: 'tpWindDraw', cam: 'pull' },
    { f: 0.42, pose: 'tpDive', hits: 1, share: 0.5, foe: 'knock', dist: 1.6, move: { kind: 'dive' }, ai: 3, fx: 'tpGroveBurst', sfx: 'tpFinisher', cam: 'shake', stop: 0.16 },
    { f: 0.55, pose: 'tpPassA', hits: 3, share: 0.16, foe: 'hit', ai: 2, fx: 'tpWindLine', sfx: 'tpPass2', stop: 0.04 },
    { f: 0.68, pose: 'tpDive', hits: 1, share: 0.3, foe: 'knock', dist: 2.2, ai: 2, fx: 'tpGroveBurst', sfx: 'tpFinisher', cam: 'shake', stop: 0.2 },
    { f: 0.8, pose: 'tpKneel', hits: 0, share: 0, foe: 'fall', fx: 'tpLeafRain', sfx: 'tpLeafRain' },
    { f: 0.9, pose: 'tpStance', hits: 0, share: 0, foe: 'recover', fx: 'tpRingFade', sfx: 'tpSheathe' },
  ],
  // spin field: the caster turns on the spot, three whirling bursts, a rising sweep, one flat release
  spin: [
    { f: 0, pose: 'tpUproot', hits: 1, share: 0.05, foe: 'pull', fx: 'tpLeafSpout', sfx: 'tpUproot', cam: 'push', stop: 0.05 },
    { f: 0.1, pose: 'tpPassA', hits: 3, share: 0.15, foe: 'spin', ai: 3, fx: 'tpWindLine', sfx: 'tpPass1', stop: 0.03 },
    { f: 0.25, pose: 'tpPassB', hits: 3, share: 0.15, foe: 'spin', ai: 3, fx: 'tpWindLine', sfx: 'tpPass2', stop: 0.03 },
    { f: 0.4, pose: 'tpPassC', hits: 4, share: 0.2, foe: 'lift', h: 0.8, ai: 4, fx: 'tpWindTriangle', sfx: 'tpPass3', cam: 'orbit', stop: 0.03 },
    { f: 0.55, pose: 'tpRise', hits: 4, share: 0.15, foe: 'lift', h: 1.4, fx: 'tpLeafVortex', sfx: 'tpVortexLoop', cam: 'orbit', stop: 0.04 },
    { f: 0.7, pose: 'tpDive', hits: 1, share: 0.3, foe: 'knock', dist: 2.0, ai: 3, fx: 'tpGroveBurst', sfx: 'tpFinisher', cam: 'shake', stop: 0.16 },
    { f: 0.8, pose: 'tpKneel', hits: 0, share: 0, foe: 'fall', fx: 'tpLeafRain', sfx: 'tpLeafRain' },
    { f: 0.9, pose: 'tpStance', hits: 0, share: 0, foe: 'recover', fx: 'tpRingFade', sfx: 'tpSheathe' },
  ],
  // throw rain (hidden weapons): three volleys of growing size, a held breath, one big throw
  throw: [
    { f: 0, pose: 'tgDraw', hits: 1, share: 0.04, foe: 'pull', fx: 'tgMistDraw', sfx: 'tgDraw', cam: 'push', stop: 0.04 },
    { f: 0.1, pose: 'tpPassA', hits: 6, share: 0.2, foe: 'hit', ai: 2, fx: 'tpWindLine', sfx: 'tpPass1', stop: 0.02 },
    { f: 0.25, pose: 'tpPassB', hits: 8, share: 0.24, foe: 'hit', ai: 3, fx: 'tpWindLine', sfx: 'tpPass2', stop: 0.02 },
    { f: 0.42, pose: 'tpPassC', hits: 10, share: 0.22, foe: 'hit', ai: 4, fx: 'tpWindTriangle', sfx: 'tpPass3', cam: 'orbit', stop: 0.02 },
    { f: 0.6, pose: 'tgRoot', hits: 0, share: 0, foe: 'hit', fx: 'tgDomeGather', cam: 'pull' },
    { f: 0.7, pose: 'tgCrush', hits: 1, share: 0.3, foe: 'knock', dist: 2.0, ai: 2, fx: 'tpGroveBurst', sfx: 'tpFinisher', cam: 'shake', stop: 0.16 },
    { f: 0.8, pose: 'tpKneel', hits: 0, share: 0, foe: 'fall', fx: 'tpLeafRain', sfx: 'tpLeafRain' },
    { f: 0.9, pose: 'tgStance', hits: 0, share: 0, foe: 'recover', fx: 'tgRingFade', sfx: 'tgExhale' },
  ],
};

export interface UltShape {
  readonly skillId: string;
  readonly name: string;
  readonly template: UltTemplate;
  /** Seconds of the piece after the catch (4–5). */
  readonly len: number;
  /** Seconds of the tell. */
  readonly tell: number;
  /** Field radius (m). */
  readonly radius: number;
  /** Total power (LONG_ULT: tempest 3.8, taiji 3.8; skeletons 3.4–4.2). */
  readonly power: number;
  /** Multiplier on the multi-blow beats' blow counts. */
  readonly blows: number;
  /** Multiplier on reach: pass distance, leap height, knock distance. */
  readonly reach: number;
  /** Rhythm: beat start = len × f^warp (below 1 pushes beats later = a slower build, above 1 earlier). */
  readonly warp: number;
}

const GROUND = new Set<UltTemplate>(['rooted', 'throw']);

/** Builds one art's ult from its template and overrides (rules: registry.registerLongMove checks it). */
export function buildUlt(s: UltShape): LongMoveDef {
  const rooted = s.template === 'rooted';
  const opener: LongOpener = {
    type: 'field',
    range: 0,
    radius: s.radius,
    tell: s.tell,
    tellPose: rooted ? 'tgOpen' : 'tpPlant',
    tellFx: rooted ? 'tgMistRing' : 'tpLeafRing',
    tellSfx: rooted ? 'tgRageFlare' : 'tpRageFlare',
  };
  const beats: LongBeat[] = TEMPLATES[s.template].map((t) => {
    const { f, ...b } = t;
    const out: { -readonly [K in keyof LongBeat]: LongBeat[K] } = {
      ...b,
      at: Math.round(s.len * Math.pow(f, s.warp) * 1000) / 1000,
    };
    if (b.hits > 1) out.hits = Math.max(2, Math.round(b.hits * s.blows));
    if (b.dist !== undefined) out.dist = Math.min(2.4, Math.round(b.dist * s.reach * 100) / 100);
    if (b.move?.dist !== undefined) out.move = { ...b.move, dist: Math.round(b.move.dist * s.reach * 100) / 100 };
    if (b.move?.h !== undefined) out.move = { ...b.move, h: Math.round(b.move.h * s.reach * 100) / 100 };
    if (b.fx === 'tgPressureWave') out.radius = Math.round((s.radius + 0.8) * 10) / 10;
    return out;
  });
  return {
    skillId: s.skillId,
    kind: 'ult',
    name: s.name,
    opener,
    len: s.len,
    power: s.power,
    lunge: 0,
    whiff: {
      pose: GROUND.has(s.template) ? 'tgSpent' : 'tpSpent',
      fx: GROUND.has(s.template) ? 'tgMistScatter' : 'tpLeafScatter',
      sfx: GROUND.has(s.template) ? 'tgSpent' : 'tpSpent',
      stop: 0.9,
      rageBack: 10,
    },
    beats,
  };
}
